import { useAutoneTranslation } from '@autone/translations';
import { useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSeparator,
  DialogTitle,
  DrawerFooter,
  InputWithIcon,
  Separator,
} from '../../../atoms';
import { EditIcon, PinnedIcon, TrashIcon, UnpinnedIcon } from '../../../icons';
import { cn } from '../../../utils';
import {
  type AdvancedFiltersConfig,
  AdvancedFiltersContent,
  type InferAdvancedFilterPublicState,
} from '../advanced-filters';
import { AdvancedFiltersStateProvider } from '../filters-drawer/providers/AdvancedFiltersStateProvider';
import { useAdvancedFiltersStateContext } from '../filters-drawer/providers/AdvancedFiltersStateProvider';
import { GeneralFiltersStateProvider } from '../filters-drawer/providers/GeneralFiltersStateProvider';
import { useGeneralFiltersStateContext } from '../filters-drawer/providers/GeneralFiltersStateProvider';
import {
  type GeneralFilterGroup,
  GeneralFiltersContent,
  type SelectedGeneralFilters,
} from '../general-filters';

import {
  useSavedFiltersActionsContext,
  useViewedSavedFilterContext,
} from './SavedFilters.context';
import { type SavedFilterTemplate } from './types';
import {
  detectInvalidAdvancedFilters,
  hydrateAdvancedFiltersFromStorage,
  sanitizeAdvancedFiltersForStorage,
} from './utilities/advanced-filters';
import { detectInvalidGeneralFilters } from './utilities/general-filters';

interface DeleteSavedFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DeleteSavedFilterDialog = ({
  open,
  onOpenChange,
  filterName,
  onConfirm,
  onCancel,
}: DeleteSavedFilterDialogProps) => {
  const { common } = useAutoneTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{common('filters.delete-saved-filter')}</DialogTitle>
        </DialogHeader>
        <DialogSeparator />
        <DialogDescription>
          {common('filters.delete-confirmation', { name: filterName })}
        </DialogDescription>
        <DialogSeparator />
        <DialogFooter>
          <Button
            id="delete-cancel-button"
            variant="outline"
            onClick={onCancel}
            aria-label={common('buttons.cancel')}
          >
            {common('buttons.cancel')}
          </Button>
          <Button
            id="delete-confirm-button"
            onClick={onConfirm}
            aria-label={common('buttons.confirm')}
          >
            {common('buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface SavedFiltersSectionHeaderProps {
  title: string;
}

export const SavedFiltersSectionHeader = ({
  title,
}: SavedFiltersSectionHeaderProps) => {
  return (
    <div className="flex items-center justify-between h-12">
      <p className="text-lg font-medium">{title}</p>
    </div>
  );
};

export const SavedFiltersList = () => {
  const { savedFilterTemplates } = useSavedFiltersActionsContext();
  const { common } = useAutoneTranslation();

  const pinnedFilters = savedFilterTemplates.filter(
    (filter) => filter.isPinned,
  );
  const unpinnedFilters = savedFilterTemplates.filter(
    (filter) => !filter.isPinned,
  );

  return (
    <div className="flex flex-col gap-4">
      {pinnedFilters.length > 0 && (
        <div className="flex flex-col gap-4">
          <SavedFiltersSectionHeader title={common('filters.pinned')} />
          <div className="flex flex-col">
            {pinnedFilters.map((savedFilterTemplate) => (
              <SavedFilterListItem
                key={savedFilterTemplate.id}
                savedFilterTemplate={savedFilterTemplate}
              />
            ))}
          </div>
        </div>
      )}

      {unpinnedFilters.length > 0 && (
        <div className="flex flex-col gap-4">
          <SavedFiltersSectionHeader title={common('filters.saved')} />
          <div className="flex flex-col">
            {unpinnedFilters.map((savedFilterTemplate) => (
              <SavedFilterListItem
                key={savedFilterTemplate.id}
                savedFilterTemplate={savedFilterTemplate}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface SavedFilterItemProps {
  savedFilterTemplate: SavedFilterTemplate;
}

const SavedFilterListItem = ({ savedFilterTemplate }: SavedFilterItemProps) => {
  const { viewedSavedFilterId, setViewedSavedFilterId } =
    useViewedSavedFilterContext();
  const isSelected = viewedSavedFilterId === savedFilterTemplate.id;

  const handleViewSavedFilter = () => {
    if (isSelected) {
      setViewedSavedFilterId(null);
    } else {
      setViewedSavedFilterId(savedFilterTemplate.id);
    }
  };

  return (
    <div
      onClick={handleViewSavedFilter}
      className={cn(
        'flex flex-row h-14 w-full border-b border-b-grey-10 hover:cursor-pointer',
        isSelected && 'bg-accent',
      )}
    >
      <SavedFilterListItemDefaultView
        savedFilterTemplate={savedFilterTemplate}
      />
    </div>
  );
};

const SavedFilterListItemDefaultView = ({
  savedFilterTemplate,
}: SavedFilterItemProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { onTogglePinSavedFilter, onDeleteSavedFilter } =
    useSavedFiltersActionsContext();
  const { setEditingSavedFilterId } = useViewedSavedFilterContext();

  const handleTogglePin = () => {
    if (onTogglePinSavedFilter) {
      onTogglePinSavedFilter(savedFilterTemplate.id);
    }
  };

  const handleEdit = () => {
    setEditingSavedFilterId(savedFilterTemplate.id);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDeleteSavedFilter) {
      onDeleteSavedFilter(savedFilterTemplate.id);
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const isPinned = savedFilterTemplate.isPinned;

  return (
    <div
      ref={containerRef}
      className="flex flex-row justify-between items-center p-4 w-full"
    >
      <span className="text-sm font-medium">{savedFilterTemplate.name}</span>
      <div>
        <Button
          id="pin-saved-filter"
          aria-label="pin-saved-filter"
          data-testid="pin-saved-filter"
          size="icon-sm"
          variant="ghost"
          onClick={handleTogglePin}
        >
          {isPinned ? (
            <PinnedIcon className="!size-4" />
          ) : (
            <UnpinnedIcon className="!size-4" />
          )}
        </Button>
        <Button
          id="edit-saved-filter"
          aria-label="Edit saved filter"
          data-testid="edit-saved-filter"
          size="icon-sm"
          variant="ghost"
          onClick={handleEdit}
        >
          <EditIcon className="!size-4" />
        </Button>
        <Button
          id="delete-saved-filter"
          aria-label="Delete saved filter"
          data-testid="delete-saved-filter"
          size="icon-sm"
          variant="ghost"
          onClick={handleDeleteClick}
        >
          <TrashIcon className="!size-4" />
        </Button>

        {/* Delete Confirmation Dialog */}
        <DeleteSavedFilterDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          filterName={savedFilterTemplate.name}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      </div>
    </div>
  );
};

export const NoSavedFilterTemplateData = () => {
  const { common } = useAutoneTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm">
      <div className="font-medium">{common('filters.no-saved-filters')}</div>
    </div>
  );
};

// Edit Saved Filter View
interface EditSavedFilterViewProps {
  savedFilterTemplate: SavedFilterTemplate<AdvancedFiltersConfig>;
  generalFiltersConfig: GeneralFilterGroup[];
  advancedFiltersConfig: AdvancedFiltersConfig;
  initialSelectedGeneralFilters?: SelectedGeneralFilters;
  initialAdvancedFilters?: InferAdvancedFilterPublicState<AdvancedFiltersConfig> | null;
}

export const EditSavedFilterView = ({
  savedFilterTemplate,
  generalFiltersConfig,
  advancedFiltersConfig,
  initialSelectedGeneralFilters,
  initialAdvancedFilters,
}: EditSavedFilterViewProps) => {
  const { setEditingSavedFilterId } = useViewedSavedFilterContext();
  const [filterName, setFilterName] = useState(savedFilterTemplate.name);

  const { valid: validGeneralFilters } = detectInvalidGeneralFilters(
    initialSelectedGeneralFilters ?? savedFilterTemplate.selectedGeneralFilters,
    generalFiltersConfig,
  );

  const { valid: validAdvancedFilters } = detectInvalidAdvancedFilters(
    initialAdvancedFilters ??
      hydrateAdvancedFiltersFromStorage(savedFilterTemplate.advancedFilters),
    advancedFiltersConfig,
  );

  const handleBack = () => {
    setEditingSavedFilterId(null);
  };

  return (
    <GeneralFiltersStateProvider
      filterGroups={generalFiltersConfig}
      selectedGeneralFilters={validGeneralFilters}
    >
      <AdvancedFiltersStateProvider
        advancedFiltersConfig={advancedFiltersConfig}
        selectedAdvancedFilters={validAdvancedFilters}
      >
        <EditSavedFilterForm
          filterName={filterName}
          onFilterNameChange={setFilterName}
          savedFilterTemplate={savedFilterTemplate}
          onCancel={handleBack}
        />
      </AdvancedFiltersStateProvider>
    </GeneralFiltersStateProvider>
  );
};

interface EditSavedFilterFormProps {
  filterName: string;
  onFilterNameChange: (value: string) => void;
  savedFilterTemplate: SavedFilterTemplate;
  onCancel: () => void;
}

const EditSavedFilterForm = ({
  filterName,
  onFilterNameChange,
  savedFilterTemplate,
  onCancel,
}: EditSavedFilterFormProps) => {
  const { common } = useAutoneTranslation();
  const { setEditingSavedFilterId } = useViewedSavedFilterContext();
  const { onEditSavedFilterName, onEditSavedFilter } =
    useSavedFiltersActionsContext();
  const { getPreparedGeneralFilters } = useGeneralFiltersStateContext();
  const { localAdvancedFilters } = useAdvancedFiltersStateContext();

  const handleSave = () => {
    if (!filterName.trim()) return;

    const selectedGeneralFilters = getPreparedGeneralFilters();
    const advancedFilters =
      sanitizeAdvancedFiltersForStorage(localAdvancedFilters);

    onEditSavedFilter?.(savedFilterTemplate.id, {
      name: filterName.trim(),
      selectedGeneralFilters,
      advancedFilters,
    });

    if (filterName.trim() !== savedFilterTemplate.name) {
      onEditSavedFilterName?.(savedFilterTemplate.id, filterName.trim());
    }

    setEditingSavedFilterId(null);
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium">
          {common('filters.edit-saved-filter')}
        </h2>
      </div>

      <div className="flex-1 p-6 space-y-6 overflow-y-auto">
        <div className="space-y-2">
          <label htmlFor="filter-name" className="text-sm font-medium">
            {common('filters.filter-name')}
          </label>
          <InputWithIcon
            id="filter-name-input"
            value={filterName}
            onChange={(e) => onFilterNameChange(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="space-y-3">
          <GeneralFiltersContent />
        </div>

        <div className="space-y-3">
          <AdvancedFiltersContent />
        </div>
      </div>

      <Separator />
      <DrawerFooter className="flex-row justify-between">
        <Button
          id="cancel-edit-button"
          variant="outline"
          onClick={onCancel}
          aria-label="Cancel edit"
        >
          {common('buttons.cancel')}
        </Button>
        <Button
          id="Save-filters-button"
          onClick={handleSave}
          disabled={!filterName.length}
          aria-label="Save filters"
        >
          {common('buttons.save-changes')}
        </Button>
      </DrawerFooter>
    </div>
  );
};
